import { ELEMENT_ATTRIBUTE_GROUP, ELEMENT_GROUPS } from './../utils';
import fonts from '../../../../utils/Fonts';

const behaviourFields = [
  {
    label: 'Open Action Sheet by Local Store Value',
    name: 'localStoreOpenSheetField',
    description:
      'If true, when the local store contains the specified key with the value true, the actionsheet will open. This can be used as follows: a button triggers a sequence that updates the local store which triggers the file upload actionsheet',
    editor: 'textbox',
    default: '',
    group: ELEMENT_ATTRIBUTE_GROUP.BEHAVIOUR
  },
  {
    label: 'Hide Placeholder Area',
    name: 'hidePlacerholderArea',
    description:
      'If true, the placeholder that is tappable and triggers the actionsheet, will be hidden.',
    editor: 'switch',
    default: false,
    group: ELEMENT_ATTRIBUTE_GROUP.BEHAVIOUR
  },
  {
    label: 'Show Uploaded Date',
    name: 'showUploadedDate',
    description:
      'If true, the date an image was uploaded will be shown below each image.',
    editor: 'switch',
    default: false,
    group: ELEMENT_ATTRIBUTE_GROUP.BEHAVIOUR
  },
  {
    label: 'Multiple Uploads',
    name: 'allowMultipleUploads',
    description:
      'If true, allows multiple file uploads. If false, uses min qty and max qty to control the number of uploads allowed.',
    editor: 'switch',
    default: true,
    group: ELEMENT_ATTRIBUTE_GROUP.BEHAVIOUR
  },
  {
    label: 'Can Upload From Device Library',
    name: 'allowImageLibraryUpload',
    description:
      'If true, allows images to be uploaded from the mobile devices photo library',
    editor: 'switch',
    default: true,
    group: ELEMENT_ATTRIBUTE_GROUP.BEHAVIOUR
  },
  {
    label: 'Camera Upload',
    name: 'allowCameraUpload',
    description: 'If true, images can be uploaded straight from camera',
    editor: 'switch',
    default: true,
    group: ELEMENT_ATTRIBUTE_GROUP.BEHAVIOUR
  },
  {
    label: 'Can Upload Documents',
    name: 'allowDocumentUpload',
    description:
      'If true, documents or non-image files can be uploaded from the device',
    editor: 'switch',
    default: true,
    group: ELEMENT_ATTRIBUTE_GROUP.BEHAVIOUR
  },
  {
    label: 'Min Qty',
    name: 'minQty',
    description:
      'Minimum number of uploads required. Set to zero if the uploading of files is optional. If the user does not upload the required number of files, they will be shown the validation error message.',
    editor: 'number',
    default: 1,
    group: ELEMENT_ATTRIBUTE_GROUP.BEHAVIOUR
  },
  {
    label: 'Max Qty',
    name: 'maxQty',
    description: 'Maximum number of uploads allowed',
    editor: 'number',
    default: 1,
    displayWhen: [
      { field: 'allowMultipleUploads', operator: '=', value: false }
    ],
    group: ELEMENT_ATTRIBUTE_GROUP.BEHAVIOUR
  },
  {
    label: 'Store in UserLibrary',
    name: 'storeInUserLibrary',
    description:
      'Whether the files or images will be stored in the firestore user library. This is a folder with the same name as the firebase id of the current user.',
    editor: 'switch',
    default: true,
    group: ELEMENT_ATTRIBUTE_GROUP.BEHAVIOUR
  },
  {
    label: 'Storage Folder Path',
    name: 'storagePath',
    description:
      'The path in firebase storage where the files should be stored. Use a placeholder path using data source ids. i.e. projects/${ds.datasourceId.id}/homes where datasourceId is the ID of the datasource definition record.',
    editor: 'textbox',
    default: '',
    group: ELEMENT_ATTRIBUTE_GROUP.BEHAVIOUR,
    displayWhen: [
      { field: 'storeInUserLibrary', operator: '!==', value: false }
    ]
  },
  {
    label: 'File Name',
    name: 'fileName',
    description:
      'A name of a file to use in cloud storage. This accepts parameters.',
    editor: 'textbox',
    default: '',
    group: ELEMENT_ATTRIBUTE_GROUP.BEHAVIOUR,
    displayWhen: []
  },
  {
    label: 'Thumbnail Width',
    name: 'thumbnailWidth',
    editor: 'number',
    description:
      'The width of the generated thumbnail, if the file uploaded is an image. Defaults to 100.',
    default: 320,
    options: [],
    group: ELEMENT_ATTRIBUTE_GROUP.LAYOUT
  },
  {
    label: 'Thumbnail Height',
    name: 'thumbnailHeight',
    editor: 'number',
    description:
      'The height of the generated thumbnail, if the file uploaded is an image. Defaults to 100.',
    default: 200,
    options: [],
    group: ELEMENT_ATTRIBUTE_GROUP.LAYOUT
  },
  {
    label: 'Thumbnail Resize Option',
    name: 'thumbnailResizeOptions',
    editor: 'dropdown',
    description:
      'The resize option of the thumbnail, if the file uploaded is an image.',
    default: 'cover',
    options: [
      { label: 'Cover', value: 'cover' },
      { label: 'Contain', value: 'contain' }
    ],
    group: ELEMENT_ATTRIBUTE_GROUP.BEHAVIOUR
  }
];

const contentFields = [
  {
    label: 'Data Binding Source',
    name: 'datasourceId',
    description: 'Choose a content type this element will get its data from',
    editor: 'dropdown',
    function: {
      name: 'getDynamicDataSources',
      params: ['app.id']
    },
    default: '',
    displayWhen: [],
    group: ELEMENT_ATTRIBUTE_GROUP.CONTENT
  },
  {
    label: 'Data Binding Field',
    name: 'sourceField',
    editor: 'dropdown',
    description:
      'The field from the underlying datasource will be used as the value of this element',
    default: '',
    options: [],
    function: {
      name: 'getDynamicDataSourceFields',
      values: [],
      params: ['app.id', 'element.datasourceId']
    },
    group: ELEMENT_ATTRIBUTE_GROUP.CONTENT
  },
  {
    label: 'Image Upload Button Text',
    name: 'imageUploadButtonText',
    description:
      'The text that appears on the action sheet for uploading an image from the devices photo library',
    editor: 'textbox',
    default: 'Select Image...',
    displayWhen: [
      { field: 'allowImageLibraryUpload', operator: '=', value: true }
    ],
    group: ELEMENT_ATTRIBUTE_GROUP.CONTENT
  },
  {
    label: 'Document Upload Button Text',
    name: 'documentUploadButtonText',
    description:
      'The text that appears on the action sheet for uploading a document.',
    editor: 'textbox',
    default: 'Select Document...',
    displayWhen: [{ field: 'allowDocumentUpload', operator: '=', value: true }],
    group: ELEMENT_ATTRIBUTE_GROUP.CONTENT
  },
  {
    label: 'Camera Upload Button Text',
    name: 'cameraUploadButtonText',
    description: 'The text that appears on the camera upload button',
    editor: 'textbox',
    default: 'Take Photo...',
    displayWhen: [{ field: 'allowCameraUpload', operator: '=', value: true }],
    group: ELEMENT_ATTRIBUTE_GROUP.CONTENT
  },
  {
    label: 'Validation Error Message',
    name: 'validationErrorMessage',
    editor: 'textbox',
    description:
      'The error message to display if the required number of files are not uploaded.',
    default: 'Please upload the required images/files.',
    options: [],
    group: ELEMENT_ATTRIBUTE_GROUP.CONTENT
  }
];

const imageFields = [
  {
    label: 'Disable Pre-Upload Resize',
    name: 'disablePreUploadResize',
    description:
      'If true, always uploads the original image from the camera or photo library without resizing.',
    editor: 'switch',
    default: true,
    group: ELEMENT_ATTRIBUTE_GROUP.IMAGERY
  },
  {
    label: 'Pre-Upload Resize Percent',
    name: 'preUploadResizePercentage',
    description:
      'Defaults to 10%. A value between 0 and 100 to reduce the image dimenions to. For example if the camera takes a picture at a height of 2448 and width of 600, and we resize by 10% factor, the result image is resized to 2448 * 0.1 height and 600 * 0.1 width',
    editor: 'number',
    default: 10,
    group: ELEMENT_ATTRIBUTE_GROUP.IMAGERY
  },
  {
    label: 'Use Placeholder Image',
    name: 'usePlaceholderImage',
    description:
      'If true, shows a placeholder image that the user can tap, otherwise shows a default placeholder image that is stored in the mobile app.',
    editor: 'switch',
    default: true,
    group: ELEMENT_ATTRIBUTE_GROUP.IMAGERY
  },
  {
    label:
      'Placeholder Image (Shows Action Sheet or Image Library when tapped)',
    name: 'placeholderImage',
    description: 'An image to display as a placeholder image',
    editor: 'filebrowse',
    default: '',
    mediaLibraryPath: 'apps',
    mediaLibraryStoreIn: 'custom_folder',
    allowMultipleUploads: false,
    maximumUploads: 1,
    displayWhen: [{ field: 'usePlaceholderImage', operator: '=', value: true }],
    group: ELEMENT_ATTRIBUTE_GROUP.IMAGERY
  }
];

const layoutFields = [
  'layout',
  'margin',
  {
    label: 'Placeholder Image Width',
    name: 'placeholderImageWidth',
    description: 'The width of the placeholder image',
    editor: 'number',
    default: 320,
    displayWhen: [{ field: 'usePlaceholderImage', operator: '=', value: true }],
    group: ELEMENT_ATTRIBUTE_GROUP.LAYOUT
  },
  {
    label: 'Placeholder Image Height',
    name: 'placeholderImageHeight',
    description: 'The height of the placeholder image',
    editor: 'number',
    default: 200,
    displayWhen: [{ field: 'usePlaceholderImage', operator: '=', value: true }],
    group: ELEMENT_ATTRIBUTE_GROUP.LAYOUT
  },
  {
    label: 'Upload Spinner Size',
    name: 'uploadSpinnerSize',
    editor: 'dropdown',
    description: 'The size of the upload spinner.',
    default: 'small',
    options: [
      { label: 'Small', value: 'small' },
      { label: 'Large', value: 'large' }
    ],
    group: ELEMENT_ATTRIBUTE_GROUP.LAYOUT
  },
  {
    label: 'Border Radius',
    name: 'borderRadius',
    editor: 'number',
    description: 'The border radius of the element',
    default: 0,
    group: ELEMENT_ATTRIBUTE_GROUP.LAYOUT
  },
  {
    label: 'Border Width',
    name: 'borderWidth',
    editor: 'number',
    description: 'The border width of the element',
    default: 0,
    group: ELEMENT_ATTRIBUTE_GROUP.LAYOUT
  },
  {
    label: 'Border Style',
    name: 'borderStyle',
    editor: 'dropdown',
    description: 'The border style of the element',
    default: 'solid',
    options: [
      { label: 'Solid', value: 'solid' },
      { label: 'Dotted', value: 'dotted' },
      { label: 'Dashed', value: 'dashed' }
    ],
    group: ELEMENT_ATTRIBUTE_GROUP.LAYOUT
  },
  {
    label: 'Image Border Radius',
    name: 'imageBorderRadius',
    editor: 'number',
    description: 'The border radius of the images',
    default: 0,
    group: ELEMENT_ATTRIBUTE_GROUP.LAYOUT
  },
  {
    label: 'Image Border Width',
    name: 'imageBorderWidth',
    editor: 'number',
    description: 'The border width of the images',
    default: 0,
    group: ELEMENT_ATTRIBUTE_GROUP.LAYOUT
  },
  {
    label: 'Image Border Style',
    name: 'imageBorderStyle',
    editor: 'dropdown',
    description: 'The border style of the images',
    default: 'solid',
    options: [
      { label: 'Solid', value: 'solid' },
      { label: 'Dotted', value: 'dotted' },
      { label: 'Dashed', value: 'dashed' }
    ],
    group: ELEMENT_ATTRIBUTE_GROUP.LAYOUT
  }
];

const colorFields = [
  {
    label: 'Upload Spinner Color',
    name: 'uploadSpinnerColor',
    editor: 'color',
    description: 'The color of the activity indicator during upload.',
    default: '#182fa4',
    group: ELEMENT_ATTRIBUTE_GROUP.COLORS
  },
  {
    label: 'Border Color',
    name: 'borderColor',
    editor: 'color',
    description: 'The border color of a list item',
    default: '#182fa4',
    group: ELEMENT_ATTRIBUTE_GROUP.COLORS
  },
  {
    label: 'Image Date Color',
    name: 'imageDateColor',
    editor: 'color',
    description: 'The border color of a list item',
    default: '#182fa4',
    displayWhen: [{ field: 'showUploadedDate', operator: '=', value: true }],
    group: ELEMENT_ATTRIBUTE_GROUP.COLORS
  }
];

const fontFields = [
  {
    name: 'imageDateFontFamily',
    label: 'Image Date Font Family',
    description: 'The font family for the dates that appear below images',
    options: [{ label: '<Not Set>', value: '' }, ...fonts],
    editor: 'dropdown',
    type: 'string',
    group: ELEMENT_ATTRIBUTE_GROUP.FONTS
  },
  {
    name: 'imageDateFont',
    label: 'Image Date Font',
    description: '',
    editor: 'fontsize',
    type: 'font',
    default: 16,
    group: ELEMENT_ATTRIBUTE_GROUP.FONTS
  }
];

const FileUploadElementFields = {
  group: ELEMENT_GROUPS.MEDIA,
  label: 'File Upload',
  icon: 'ArrowUpTrayIcon',
  description: 'Adds a file upload form control',
  properties: [
    ...behaviourFields,
    ...contentFields,
    ...imageFields,
    ...layoutFields,
    ...colorFields,
    ...fontFields
  ]
};

export default FileUploadElementFields;
